import {
  ChangeDetectionStrategy,
  Component,
  output,
  input,
  computed,
  TemplateRef,
} from '@angular/core';

import type { Candidate } from '@core/candidate-types';
import { TranslatePipe } from '@core/i18n-legacy';
import type {
  Admin,
  HiddenResultPreview,
  InterviewResult,
  ResultGroup,
  ScorecardsResult,
} from '@core/interview-results-types';
import type { Guest } from '@core/notes-types';
import type { Placement } from '@core/placements-types';
import { JobSkillsSummaryDetails } from '@core/positions-and-skills-types';
import { JobSkillsSummaryDetailsComponent } from '@core/positions-and-skills-ui';
import type { Stage } from '@core/stages-types';
import { Theme } from '@core/theme-util';
import { BoxComponent, DividerComponent, HeadingComponent } from '@core/user-interface-legacy';
import { groupByKey, uniqByKey } from '@core/utils-legacy';

import { InterviewResultSummaryOverallComponent } from './interview-result-summary-overall/interview-result-summary-overall.component';
import { InterviewResultSummaryScorecardsComponent } from './interview-result-summary-scorecards/interview-result-summary-scorecards.component';

type InterviewRating = {
  count: number;
  rating: string;
  admins: Array<InterviewResult['admin']>;
  guests: Array<Guest | undefined>;
};

@Component({
  selector: 'rt-interview-result-summary-list',
  templateUrl: 'interview-result-summary-list.component.html',
  styleUrls: ['./interview-result-summary-list.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    BoxComponent,
    HeadingComponent,
    DividerComponent,
    TranslatePipe,
    JobSkillsSummaryDetailsComponent,
    InterviewResultSummaryScorecardsComponent,
    InterviewResultSummaryOverallComponent,
  ],
})
export class InterviewResultSummaryListComponent {
  public readonly lastUpdate = input<string | null>(undefined!);
  public readonly candidate = input<Candidate>(undefined!);
  public readonly results = input<InterviewResult[]>();
  public readonly hiddenResultsPreview = input<HiddenResultPreview[]>();
  public readonly offerId = input<number>();
  public readonly currentUserAvailablePlacements = input<Placement[]>([]);
  public readonly hasAccessToAllJobOffers = input<boolean>();
  public readonly scorecardsResults = input<ScorecardsResult[]>([]);
  public readonly jobSkillsSummaryDetails = input<JobSkillsSummaryDetails[]>([]);
  public readonly theme = input.required<Theme>();
  public readonly hiddenEvaluationsInfoboxTpl = input<TemplateRef<unknown>>();

  public summaryResultCounterClick = output();
  public viewPosition = output<string>();

  protected groups = computed(() => {
    const stages = this.getStagesWithOffer(this.results() || [], this.hiddenResultsPreview() || []);
    return this.getGroups(this.results() || [], stages, this.hiddenResultsPreview() || []);
  });
  protected jobSkillsSummaryDetailsVisible = computed(() => {
    const offerId = this.offerId();
    return this.jobSkillsSummaryDetails().filter(summary =>
      offerId && summary.job ? summary.job.id === offerId : true,
    );
  });
  protected showJobsTitle = computed(() => {
    const candidatePlacements = this.candidate().placements;
    return candidatePlacements.length > 1 || this.jobSkillsSummaryDetails().length > 1;
  });

  private ratingsOrder: string[] = ['strong_no', 'no', 'neutral', 'yes', 'strong_yes'];

  private getStagesWithOffer(
    results: InterviewResult[],
    hiddenResultsPreview: HiddenResultPreview[],
  ): Stage[] {
    const stages = [...results, ...hiddenResultsPreview].map(r => r.stage!).filter(r => r);

    return uniqByKey(stages, 'id');
  }

  private getGroups(
    results: InterviewResult[],
    stages: Stage[],
    hiddenResultsPreview: HiddenResultPreview[],
  ): ResultGroup[] {
    const mergedResults = [...results, ...hiddenResultsPreview];

    return (
      Object.entries(this.groupBy(mergedResults, 'stageId'))
        // Object.entries returns objects' keys as strings, meanwhile mapStage expects a number
        .reverse()
        .map(entry => [Number(entry[0]), entry[1]])
        .map(this.mapStage.bind(this, stages))
    );
  }

  private getOfferName(results: Array<InterviewResult | HiddenResultPreview>): string | null {
    const offerName = results && results[0] && results[0].offer && results[0].offer.title;

    return offerName ? offerName : null;
  }

  private getOfferId(results: Array<InterviewResult | HiddenResultPreview>): number | null {
    return results?.[0]?.offerId ?? null;
  }

  private getStageName(stages: Stage[], stageId: number): string | null {
    const stage = stages.find(s => s && s.id === stageId);

    return stage && stage.name ? stage.name : null;
  }

  private getRatings(results: InterviewResult[]): InterviewRating[] {
    return Object.entries(groupByKey(results, 'rating'))
      .map(entry => this.mapResult(entry))
      .sort((a, b) => (this.compareRatings(a) > this.compareRatings(b) ? 1 : -1));
  }

  private mapStage(
    stages: Stage[],
    [rating, results]: [number, Array<InterviewResult | HiddenResultPreview>],
  ): {
    stage: string;
    offer: string;
    offerId: number;
    ratings: InterviewRating[];
    hiddenRatings: Omit<InterviewRating, 'rating'>;
  } {
    return {
      stage: this.getStageName(stages, rating)!,
      offer: this.getOfferName(results)!,
      offerId: this.getOfferId(results)!,
      ratings: this.getRatings(
        <InterviewResult[]>results.filter((result: any) => typeof result.rating !== 'undefined'),
      ),
      hiddenRatings: (<HiddenResultPreview[]>(
        results.filter((result: any) => typeof result.rating === 'undefined')
      )).map(result => ({
        count: result.count,
        admins: result.admins,
        guests: result.guests || [],
      }))[0],
    };
  }

  private mapResult([rating, result]: [string, InterviewResult[]]): InterviewRating {
    return {
      count: result.length,
      rating: <string>this.convertType(rating)!,
      admins: uniqByKey(
        result.map(r => r.admin || <Admin>{ id: null }),
        'id',
      ).filter(value => (value?.id ?? -1) > 0),
      guests: uniqByKey(
        result.map(r => r.guest || <Guest>{ id: -1 }),
        'id',
      ).filter(value => value.id > 0),
    };
  }

  private compareRatings = (rating: InterviewRating) => this.ratingsOrder.indexOf(rating.rating);

  private groupBy(
    results: Array<InterviewResult | HiddenResultPreview>,
    key: keyof InterviewResult | keyof HiddenResultPreview,
  ): { [key: string]: InterviewResult[] } {
    return (<any[]>results).reduce<Record<string, InterviewResult[]>>((agg, result) => {
      agg[result[key]] = [...(agg[result[key]] || []), result];
      return agg;
    }, {});
  }

  private convertType(value: string): string | undefined | null | boolean | number {
    const values = { undefined: undefined, null: null, true: true, false: false };
    const isNumber = !isNaN(+value);
    return (
      (isNumber && +value) ||
      (!(value in values) && value) ||
      values[<'undefined' | 'null' | 'true' | 'false'>value]
    );
  }
}
